import { useState, type KeyboardEvent } from 'react';

/**
 * One tab stop for a list of navigation links, with the arrow keys moving inside it (roving
 * tabindex). The sidebar's rail and panel use it so a keyboard user tabs PAST the navigation in
 * two stops, not one per section and page, and still reaches every entry with the arrows.
 *
 * The stop sits on `preferred` (the current section or page) until the user arrows elsewhere; a
 * new `preferred` (they moved to another page) puts it back there. Spread `itemProps(i)` on each
 * entry, in drawing order, and mark the element around them `data-roving-group`. The handlers sit
 * on the entries (links), not the list, so nothing non-interactive listens for keys.
 *
 * Only Tab order changes: every link is still in the page, so a screen reader's browse mode and
 * its links list find all of them as before.
 */
export function useRovingFocus(count: number, preferred: number) {
  const [moved, setMoved] = useState<{ at: number; from: number } | null>(null);
  const home = count > 0 ? Math.min(Math.max(preferred, 0), count - 1) : 0;
  const current = moved && moved.from === preferred && moved.at < count ? moved.at : home;

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const group = e.currentTarget.closest('[data-roving-group]');
    if (!group) return;
    const items = Array.from(group.querySelectorAll<HTMLElement>('[data-roving]'));
    const at = items.indexOf(e.currentTarget);
    if (at < 0) return;
    let next: number;
    switch (e.key) {
      case 'ArrowDown': next = (at + 1) % items.length; break;
      case 'ArrowUp': next = (at - 1 + items.length) % items.length; break;
      case 'Home': next = 0; break;
      case 'End': next = items.length - 1; break;
      default: return;
    }
    e.preventDefault();
    setMoved({ at: next, from: preferred });
    items[next].focus();
  };

  return {
    /** Tab stop (0 for the one item Tab lands on, -1 for the rest), arrow keys, and focus tracking. */
    itemProps: (i: number) => ({
      tabIndex: i === current ? 0 : -1,
      onKeyDown,
      // Keeps the stop where the user put focus (by clicking, say), so Shift+Tab back returns there.
      onFocus: () => { if (i !== current) setMoved({ at: i, from: preferred }); },
      'data-roving': true,
    }),
  };
}

export type RovingItemProps = ReturnType<ReturnType<typeof useRovingFocus>['itemProps']>;
